/**
 * Debug component to test and diagnose telemetry issues
 * Access at /debug-telemetry route
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { telemetry } from '@/telemetry-browser';
import { frontendTraceManager } from '@/telemetry/FrontendTraceManager';
import { useVideoCallTelemetry } from '@/hooks/video-call/useVideoCallTelemetry';

export function TelemetryDebug() {
  const [results, setResults] = useState<string[]>([]);

  const addResult = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const emoji = type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️';
    setResults(prev => [...prev, `[${timestamp}] ${emoji} ${message}`]);
    
    if (type === 'error') {
      console.error(message);
    } else {
      console.log(message);
    }
  };

  const testBasicTelemetry = () => {
    try {
      const span = telemetry.startSpan('debug.basic_test', {
        attributes: {
          'test.type': 'basic_span',
          'test.timestamp': Date.now(),
        }
      });
      
      addResult(`Basic span created: ${span.spanContext().traceId}`, 'success');
      span.addEvent('test_event', { message: 'Basic test successful' });
      span.end();
      addResult('Basic span ended successfully', 'success');
    } catch (error) {
      addResult(`Basic telemetry failed: ${error}`, 'error');
    }
  };

  const testFrontendTraceManager = () => {
    try {
      const sessionInfo = frontendTraceManager.getSessionInfo();
      addResult(`Frontend trace manager status: Active=${sessionInfo.isActive}, SessionID=${sessionInfo.sessionId}`, 'info');
      
      if (!sessionInfo.isActive) {
        frontendTraceManager.initializeSession({
          userId: 'debug-user',
          organizationId: 'debug-org',
        });
        addResult('Frontend trace manager initialized for debug', 'success');
      }
      
      const childSpan = frontendTraceManager.createChildSpan('debug.child_test', {
        attributes: {
          'debug.test': 'child_span_creation',
        },
      });
      
      addResult(`Child span created: ${childSpan.spanContext().traceId}`, 'success');
      childSpan.end();
      
      frontendTraceManager.addSessionEvent('debug_test_event', {
        test_type: 'manual_debug',
        timestamp: Date.now(),
      });
      addResult('Session event added successfully', 'success');
      
    } catch (error) {
      addResult(`Frontend trace manager failed: ${error}`, 'error');
    }
  };

  const testVideoCallTelemetry = async () => {
    try {
      if (typeof useVideoCallTelemetry !== 'function') throw new Error('useVideoCallTelemetry is not a function');
      addResult('Video call telemetry hook imported successfully', 'success');
      // Note: Can't actually test the hook here since we're not in a component context
      addResult('Video call telemetry hook available (requires React context to test)', 'info');
    } catch (error) {
      addResult(`Video call telemetry import failed: ${error}`, 'error');
    }
  };

  const checkEnvironmentVars = () => {
    const envVars = {
      'VITE_OTEL_ENDPOINT': import.meta.env.VITE_OTEL_ENDPOINT,
      'VITE_OTEL_CONSOLE_EXPORT': import.meta.env.VITE_OTEL_CONSOLE_EXPORT,
      'VITE_OTEL_SERVICE_NAME': import.meta.env.VITE_OTEL_SERVICE_NAME,
      'VITE_OTEL_HEADERS': import.meta.env.VITE_OTEL_HEADERS,
      'MODE': import.meta.env.MODE,
    };

    addResult('Environment variables:', 'info');
    Object.entries(envVars).forEach(([key, value]) => {
      addResult(`  ${key}: ${value || 'undefined'}`, value ? 'success' : 'error');
    });
  };

  const clearResults = () => {
    setResults([]);
  };

  const exportDebugInfo = () => {
    const debugInfo = {
      timestamp: new Date().toISOString(),
      environment: import.meta.env.MODE,
      telemetryConfig: {
        endpoint: import.meta.env.VITE_OTEL_ENDPOINT,
        consoleExport: import.meta.env.VITE_OTEL_CONSOLE_EXPORT,
        serviceName: import.meta.env.VITE_OTEL_SERVICE_NAME,
      },
      frontendTraceManager: frontendTraceManager.getSessionInfo(),
      results: results,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };
    
    const blob = new Blob([JSON.stringify(debugInfo, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `telemetry-debug-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    addResult('Debug info exported to JSON file', 'success');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-card rounded-lg shadow-lg p-6">
        <h1 className="text-3xl font-bold mb-6">🔧 Telemetry Debug Dashboard</h1>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Button onClick={testBasicTelemetry} variant="outline">
            🧪 Test Basic Telemetry
          </Button>
          
          <Button onClick={testFrontendTraceManager} variant="outline">
            🌟 Test Frontend Trace Manager
          </Button>
          
          <Button onClick={testVideoCallTelemetry} variant="outline">
            📹 Test Video Call Telemetry
          </Button>
          
          <Button onClick={checkEnvironmentVars} variant="outline">
            🔍 Check Environment
          </Button>
          
          <Button onClick={exportDebugInfo} variant="outline">
            📥 Export Debug Info
          </Button>
          
          <Button onClick={clearResults} variant="destructive">
            🧹 Clear Results
          </Button>
        </div>

        <div className="bg-muted rounded-lg p-4 max-h-96 overflow-y-auto">
          <h3 className="text-lg font-semibold mb-3">Debug Results ({results.length})</h3>
          
          {results.length === 0 ? (
            <p className="text-muted-foreground italic">No debug tests run yet. Click buttons above to start testing.</p>
          ) : (
            <div className="space-y-1">
              {results.map((result, index) => (
                <div key={index} className="text-sm font-mono bg-card p-2 rounded border">
                  {result}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="mt-6 p-4 bg-secondary rounded-lg">
          <h4 className="font-semibold text-foreground mb-2">What to Look For:</h4>
          <ul className="text-sm text-foreground space-y-1">
            <li>• <strong>VITE_OTEL_ENDPOINT</strong> should be set to your OTEL collector URL</li>
            <li>• <strong>Basic telemetry test</strong> should create spans successfully</li>
            <li>• <strong>Frontend trace manager</strong> should initialize without errors</li>
            <li>• Check browser Network tab for OTEL exports to collector</li>
            <li>• Check browser console for additional telemetry logs</li>
          </ul>
        </div>
      </div>
    </div>
  );
}